/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Output,
	forwardRef,
	inject,
	input,
	viewChild
} from "@angular/core";
import {
	FormsModule,
	NG_VALUE_ACCESSOR,
	type ControlValueAccessor,
} from "@angular/forms";
import type { ItemsService } from "@app/@classes/abstract/items-service-interface";
import type { TablesOrViews, TablesOrViewsKeys } from "@app/@classes/supabase";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { ChipComponent } from "../chip/chip.component";
import {
	SelectComponent,
	type CbGroupedSelectOption,
	type CbSelectOption,
} from "../select/select.component";

export type CbTagOption = CbSelectOption & {
	color?: Exclude<typeof ChipComponent.prototype.color, "mapped">;
} & ({ color: "auto" } | { color: "custom"; customColor: string });

export interface CbGroupedTagsOption extends CbGroupedSelectOption {
	options: CbTagOption[];
}

export type CbAsyncTags<T extends TablesOrViews<TablesOrViewsKeys>> = {
	service: ItemsService<T>;
	valueKey: keyof T;
} & ({ labelKey: keyof T } | { labelCallback: (item: T) => string });

export type CbAsyncGroupedTags<T extends TablesOrViews<TablesOrViewsKeys>> = {
	service: ItemsService<T>;
	valueKey: keyof T;
	groupBy: keyof T;
} & ({ labelKey: keyof T } | { labelCallback: (item: T) => string }) &
	({ groupLabelKey: keyof T } | { groupLabelCallback: (item: T) => string });

const noop = () => { };

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => TagsInputComponent),
	multi: true,
};

@Component({
	selector: "cb-tags-input",
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ChipComponent,
		SelectComponent,
		TranslateModule,
	],
	templateUrl: "./tags-input.component.html",
	styleUrl: "./tags-input.component.scss",
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagsInputComponent implements ControlValueAccessor {
	private readonly translateService: TranslateService =
		inject(TranslateService);
	private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

	/**
	 * The options to display in the select.
	 */
	public readonly options = input<CbTagOption[]>([]);

	/**
	 * The grouped options to display in the select.
	 */
	public readonly groupedOptions = input<CbGroupedTagsOption[]>([]);

	/**
	 * The options to disable in the select.
	 */
	public readonly disabledOptions = input<unknown[]>([]);

	public readonly disableCheckCallback = input<(option: CbTagOption) => boolean>(() => false);

	/**
	 * If the filtering is enabled
	 */
	public readonly filter = input<boolean>();

	/**
	 * If the translate is enabled
	 */
	public readonly translateGroupLabels = input<boolean>();

	/**
	 * If the translate is enabled
	 */
	public readonly translateOptions = input<boolean>();

	/**
	 * The async options to display in the select.
	 */
	public readonly async = input<CbAsyncTags<TablesOrViews<TablesOrViewsKeys>>>();

	/**
	 * The async grouped options to display in the select.
	 */
	public readonly groupedAsync = input<CbAsyncGroupedTags<TablesOrViews<TablesOrViewsKeys>>>();

	/**
	 * Placeholder of the input
	 */
	public readonly placeholder = input<string>();

	/**
	 * Label of the input
	 */
	public readonly label = input<string>();

	/**
	 * Hint of the input
	 */
	public readonly hint = input<string>();

	/**
	 * Info of the input displayed with a question mark
	 * when this is defined as trailing icon
	 */
	public readonly info = input<string>();

	/**
	 * If the input is required
	 */
	public readonly required = input<boolean>();

	/**
	 * If the input is disabled
	 */
	public readonly disabled = input<boolean>();

	/**
	 * Leading icon of the input
	 */
	public readonly icon = input<string>();

	/**
	 * Whether to show the reset button
	 */
	public readonly resetButton = input<boolean | undefined>(true);

	/**
	 * Whether the input has an error
	 */
	public readonly error = input<boolean>();

	/**
	 * Emits the value of the input on change
	 */
	@Output() public change: EventEmitter<unknown> = new EventEmitter<unknown>();

	readonly selectComponent = viewChild.required(SelectComponent);

	private destroy$ = new Subject<void>();

	/**
	 * The internal data model
	 */
	private innerValue: CbTagOption[] = [];

	ngOnInit() { }

	public get canReset(): boolean {
		return this.resetButton() === true && this.value.length > 0;
	}

	/**
	 * Returns the value of the input
	 */
	public get value(): CbTagOption[] {
		return this.innerValue;
	}

	/**
	 * Sets the value of the input
	 */
	public set value(v: CbTagOption[]) {
		if (v !== this.innerValue) {
			this.innerValue = v;
			this.onChangeCallback(this.value);
			this.change.emit(this.value);
		}
	}

	public get selectDisableCheckCallback(): (option: CbSelectOption) => boolean {
		return (option: CbSelectOption) =>
			this.disableCheckCallback()(option as CbTagOption);
	}

	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public writeValue(obj: any): void {
		if (obj !== this.innerValue) {
			this.innerValue = obj;
			this.cdr.detectChanges();
		}
	}

	public onTouchedCallback: () => void = noop;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public onChangeCallback: (_: any) => void = noop;

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}

	public reset(): void {
		this.value = [];
	}

	public removeTag(tag: CbTagOption): void {
		console.log(this.value.indexOf(tag));
		this.selectComponent().removeValueAtIndex(
			// biome-ignore lint/suspicious/noExplicitAny: <explanation>
			this.value.indexOf(tag.value as any),
		);
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
